import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Stars } from "@/components/Stars";
import { PhotoDialog } from "@/components/admin/PhotoDialog";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { ShieldCheck, Users, Home, UserRound, Sparkles, CalendarCheck, CheckCircle2, ImageIcon, Search, Camera, Crown, Download } from "lucide-react";

async function downloadCsv(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = Object.assign(document.createElement("a"), { href: url, download: filename });
  a.click();
  URL.revokeObjectURL(url);
}

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : "—");
const TIERS = ["free", "plus", "premium"];

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="chenia-card p-5 flex items-center gap-4" data-testid={testid}>
      <span className="grid place-items-center h-11 w-11 rounded-2xl bg-secondary text-primary shrink-0"><Icon className="h-5 w-5" /></span>
      <div><p className="font-serif text-3xl font-semibold leading-none">{value ?? "—"}</p><p className="text-xs text-muted-foreground mt-1">{label}</p></div>
    </div>
  );
}

function Overview() {
  const { t } = useLang();
  const { data } = useQuery({ queryKey: ["admin", "overview"], queryFn: async () => (await api.get("/admin/overview")).data });
  const tiers = data?.tiers || {};
  return (
    <div className="space-y-4" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Users} label={t("accounts_total")} value={data?.users} testid="stat-users" />
        <Stat icon={Home} label={t("households_total")} value={data?.households} testid="stat-households" />
        <Stat icon={UserRound} label={t("members_total")} value={data?.members} testid="stat-members" />
        <Stat icon={CalendarCheck} label={t("plans_total")} value={data?.plans} testid="stat-plans" />
        <Stat icon={Sparkles} label={t("new_7d")} value={data?.new_7d} testid="stat-new7" />
        <Stat icon={Sparkles} label={t("new_30d")} value={data?.new_30d} testid="stat-new30" />
        <Stat icon={CheckCircle2} label={t("tracked_7d")} value={data?.tracked_meals_7d} testid="stat-tracked" />
        <Stat icon={ImageIcon} label={t("custom_photos")} value={data?.custom_photos} testid="stat-photos" />
      </div>
      <div className="chenia-card p-5" data-testid="tier-split">
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">{t("col_tier")}</p>
        <div className="flex h-3 rounded-full overflow-hidden bg-secondary">
          {TIERS.map((k) => <span key={k} style={{ width: `${data?.users ? (100 * (tiers[k] || 0)) / data.users : 0}%` }} className={k === "free" ? "bg-chenia-sand" : k === "plus" ? "bg-chenia-sage" : "bg-primary"} />)}
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-sm">
          {TIERS.map((k) => <span key={k} data-testid={`tier-count-${k}`}><span className={`inline-block h-2.5 w-2.5 rounded-full mr-1.5 ${k === "free" ? "bg-chenia-sand" : k === "plus" ? "bg-chenia-sage" : "bg-primary"}`} />{t(`tier_${k}`)} · <b>{tiers[k] || 0}</b></span>)}
        </div>
      </div>
    </div>
  );
}

function Accounts() {
  const { t } = useLang();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const { data } = useQuery({ queryKey: ["admin", "users", q], queryFn: async () => (await api.get("/admin/users", { params: { q } })).data });
  const setTier = async (u, plan_tier) => {
    try {
      await api.patch(`/admin/users/${u.user_id}/tier`, { plan_tier });
      toast.success(t("tier_updated"));
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  return (
    <div className="space-y-3" data-testid="admin-accounts">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_accounts")} className="pl-9 rounded-xl" data-testid="accounts-search" />
        </div>
        <Button variant="outline" onClick={() => downloadCsv(`/admin/export/users.csv?q=${encodeURIComponent(q)}`, "chenia-comptes.csv")} className="rounded-xl" data-testid="export-users-csv"><Download className="h-4 w-4 mr-1" /> {t("export_csv")}</Button>
      </div>
      <div className="chenia-card overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground"><tr className="text-left border-b border-border">
            <th className="p-3">{t("col_email")}</th><th className="p-3">{t("col_name")}</th><th className="p-3">{t("col_tier")}</th><th className="p-3">{t("col_household")}</th><th className="p-3">{t("col_language")}</th><th className="p-3">{t("col_activity")}</th><th className="p-3">{t("col_created")}</th>
          </tr></thead>
          <tbody>
            {(data?.users || []).map((u) => (
              <tr key={u.user_id} className="border-b border-border/50 last:border-0 hover:bg-secondary/40" data-testid={`account-row-${u.email}`}>
                <td className="p-3 font-medium">{u.email}{u.role === "admin" && <ShieldCheck className="inline h-3.5 w-3.5 ml-1 text-primary" />}</td>
                <td className="p-3">{u.first_name || "—"}</td>
                <td className="p-3">
                  <select value={u.plan_tier} onChange={(e) => setTier(u, e.target.value)} data-testid={`tier-select-${u.email}`}
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold border-0 ${u.plan_tier === "premium" ? "bg-primary text-primary-foreground" : u.plan_tier === "plus" ? "bg-chenia-sage" : "bg-secondary"}`}>
                    {TIERS.map((k) => <option key={k} value={k}>{t(`tier_${k}`)}</option>)}
                  </select>
                </td>
                <td className="p-3">{u.household_size || "—"}</td>
                <td className="p-3 uppercase">{u.language}</td>
                <td className="p-3 text-muted-foreground">{fmtDate(u.last_activity)}</td>
                <td className="p-3 text-muted-foreground">{fmtDate(u.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Subscriptions() {
  const { t } = useLang();
  const { data } = useQuery({ queryKey: ["admin", "subscriptions"], queryFn: async () => (await api.get("/admin/subscriptions")).data });
  const list = data?.transactions || [];
  return (
    <div className="space-y-3">
    <div className="flex justify-end"><Button variant="outline" onClick={() => downloadCsv("/admin/export/subscriptions.csv", "chenia-abonnements.csv")} className="rounded-xl" data-testid="export-subscriptions-csv"><Download className="h-4 w-4 mr-1" /> {t("export_csv")}</Button></div>
    <div className="chenia-card overflow-x-auto" data-testid="admin-subscriptions">
      {list.length === 0 ? <p className="p-6 text-sm text-muted-foreground">{t("no_transactions")}</p> : (
        <table className="w-full text-sm min-w-[560px]">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground"><tr className="text-left border-b border-border">
            <th className="p-3">{t("col_email")}</th><th className="p-3">{t("col_tier")}</th><th className="p-3">{t("col_status")}</th><th className="p-3">{t("col_payment")}</th><th className="p-3">{t("col_date")}</th>
          </tr></thead>
          <tbody>
            {list.map((x, i) => (
              <tr key={`${x.email}-${x.created_at}-${i}`} className="border-b border-border/50 last:border-0" data-testid={`subscription-row-${i}`}>
                <td className="p-3 font-medium">{x.email || "—"}</td>
                <td className="p-3">{x.tier ? <span className="inline-flex items-center gap-1"><Crown className="h-3 w-3 text-primary" /> {t(`tier_${x.tier}`)}</span> : "—"}</td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${x.status === "completed" ? "bg-chenia-sage/50" : x.status === "failed" ? "bg-destructive/10 text-destructive" : "bg-secondary"}`}>{x.status}</span></td>
                <td className="p-3 text-muted-foreground">{x.payment_status}</td>
                <td className="p-3 text-muted-foreground">{fmtDate(x.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
    </div>
  );
}

function TopList({ title, items, render, testid }) {
  const { t, tv } = useLang();
  return (
    <div className="chenia-card p-5" data-testid={testid}>
      <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">{title}</p>
      {!items?.length ? <p className="text-sm text-muted-foreground">{t("no_data_yet")}</p> : (
        <ol className="space-y-2">
          {items.map((r, i) => (
            <li key={r.recipe_id} className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground w-4">{i + 1}</span>
              <img src={r.image} alt="" className="h-10 w-10 rounded-xl object-cover" style={{ objectPosition: r.focal || "50% 50%" }} />
              <span className="flex-1 min-w-0 truncate text-sm font-medium">{tv(r.name)}</span>
              <span className="text-xs text-muted-foreground shrink-0">{render(r)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function RecipesInsights() {
  const { t } = useLang();
  const { data } = useQuery({ queryKey: ["admin", "recipes"], queryFn: async () => (await api.get("/admin/recipes")).data });
  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="admin-recipes">
      <TopList title={t("most_cooked")} items={data?.most_cooked} render={(r) => `${r.count} ${t("times")}`} testid="top-cooked" />
      <TopList title={t("most_favorited")} items={data?.most_favorited} render={(r) => `♥ ${r.count}`} testid="top-favorited" />
      <TopList title={t("best_rated")} items={data?.best_rated} render={(r) => <Stars value={Math.round(r.avg_rating)} testid={`rated-${r.recipe_id}`} />} testid="top-rated" />
    </div>
  );
}

function Photos() {
  const { t, tv } = useLang();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const { data } = useQuery({ queryKey: ["admin", "catalog"], queryFn: async () => (await api.get("/admin/recipes/catalog")).data });
  const list = (data?.recipes || []).filter((r) => !q || tv(r.name).toLowerCase().includes(q.toLowerCase()) || r.recipe_id.includes(q.toLowerCase()));
  const onSaved = () => { qc.invalidateQueries({ queryKey: ["admin"] }); qc.invalidateQueries({ queryKey: ["recipes"] }); qc.invalidateQueries({ queryKey: ["recipe"] }); qc.invalidateQueries({ queryKey: ["plan"] }); };
  return (
    <div className="space-y-3" data-testid="admin-photos">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_recipes")} className="pl-9 rounded-xl" data-testid="photos-search" />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map((r) => (
          <div key={r.recipe_id} className="chenia-card overflow-hidden" data-testid={`photo-card-${r.recipe_id}`}>
            <div className="relative h-36">
              <img src={r.image} alt="" className="h-full w-full object-cover" style={{ objectPosition: r.focal || "50% 50%" }} data-testid={`photo-img-${r.recipe_id}`} />
              <span className={`absolute top-2 left-2 rounded-full px-2 py-0.5 text-[10px] font-bold ${r.custom_photo && !r.custom_photo.illustration ? "bg-primary text-primary-foreground" : r.custom_photo ? "bg-amber-100 text-amber-900" : "bg-background/90"}`} data-testid={`photo-badge-${r.recipe_id}`}>
                {r.custom_photo ? (r.custom_photo.illustration ? t("illustration_badge") : t("custom_photo_badge")) : t("default_photo_badge")}
              </span>
            </div>
            <div className="p-3 flex items-center gap-2">
              <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{tv(r.name)}</p><p className="text-[11px] text-muted-foreground">{t(r.category)} · {t(`tier_${r.tier}`)}</p></div>
              <Button size="sm" variant="outline" onClick={() => setEditing(r)} className="rounded-full shrink-0" data-testid={`change-photo-${r.recipe_id}`}><Camera className="h-3.5 w-3.5 mr-1" /> {t("change_photo")}</Button>
            </div>
          </div>
        ))}
      </div>
      <PhotoDialog recipe={editing} open={!!editing} onClose={() => setEditing(null)} onSaved={onSaved} />
    </div>
  );
}

export default function Admin() {
  const { t } = useLang();
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return (
    <AppShell>
      <div className="chenia-fade-up" data-testid="admin-page">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center h-11 w-11 rounded-2xl bg-primary text-primary-foreground"><ShieldCheck className="h-5 w-5" /></span>
          <div><h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight">{t("admin_space")}</h1><p className="text-sm text-muted-foreground">{t("admin_sub")}</p></div>
        </div>
        <Tabs defaultValue="overview" className="mt-6">
          <TabsList className="rounded-full bg-secondary flex-wrap h-auto">
            {["overview", "accounts", "subscriptions", "recipes", "photos"].map((k) => <TabsTrigger key={k} value={k} className="rounded-full" data-testid={`admin-tab-${k}`}>{t(`admin_${k}`)}</TabsTrigger>)}
          </TabsList>
          <TabsContent value="overview" className="mt-4"><Overview /></TabsContent>
          <TabsContent value="accounts" className="mt-4"><Accounts /></TabsContent>
          <TabsContent value="subscriptions" className="mt-4"><Subscriptions /></TabsContent>
          <TabsContent value="recipes" className="mt-4"><RecipesInsights /></TabsContent>
          <TabsContent value="photos" className="mt-4"><Photos /></TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
